import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { 
  MdBusinessCenter, MdEmail, MdPhone, 
  MdLocationOn, MdImage, MdCheckCircle, MdNavigateNext, MdNavigateBefore,
  MdStorefront, MdInfo, MdSave
} from "react-icons/md";

// Firebase Imports
import { db, storage, auth } from "./firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";

// --- Styled Components (Create Page-ல் உள்ள அதே Avaami Theme) ---
const Container = styled.div`
  background-color: #f0f2f5;
  min-height: 100vh;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (min-width: 768px) {
    padding: 40px 20px;
  }
`;

const FormCard = styled.div`
  background: white;
  width: 100%;
  max-width: 850px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StepperWrapper = styled.div`
  background: #fff;
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  position: relative;

  @media (min-width: 768px) {
    padding: 30px 40px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 40px;
    left: 60px;
    right: 60px;
    height: 2px;
    background: #e0e0e0;
    z-index: 0;

    @media (max-width: 480px) {
        display: none;
    }
  }
`;

const StepItem = styled.div`
  position: relative;
  z-index: 1;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80px;
`;

const StepCircle = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: ${props => props.active ? '#1a73e8' : '#fff'};
  border: 2px solid ${props => props.active || props.completed ? '#1a73e8' : '#ccc'};
  color: ${props => props.active ? '#fff' : '#777'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
  transition: 0.3s;
  
  ${props => props.completed && `
    background: #1a73e8;
    color: white;
  `}
`;

const StepLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.active ? '#1a73e8' : '#888'};
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 13px;
  }
`;

const FormContent = styled.div`
  padding: 20px;
  min-height: 350px;

  @media (min-width: 768px) {
    padding: 40px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  color: #1a202c;
  margin-bottom: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`margin-bottom: 18px;`;
const Label = styled.label`display: block; font-weight: 600; margin-bottom: 6px; color: #4a5568; font-size: 14px;`;
const Input = styled.input`
  width: 100%; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 15px; box-sizing: border-box;
  &:focus { outline: none; border-color: #1a73e8; box-shadow: 0 0 0 3px rgba(26,115,232,0.1); }
`;
const TextArea = styled.textarea`
  width: 100%; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 15px; min-height: 100px; box-sizing: border-box;
  &:focus { outline: none; border-color: #1a73e8; box-shadow: 0 0 0 3px rgba(26,115,232,0.1); }
`;
const Select = styled.select`
  width: 100%; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 15px; background: white; cursor: pointer;
`;

const UploadBox = styled.label`
  border: 2px dashed #cbd5e0; border-radius: 12px; padding: 25px; text-align: center; cursor: pointer;
  background: #f8fafc; transition: 0.2s; display: flex; flex-direction: column; align-items: center;
  &:hover { border-color: #1a73e8; background: #f0f7ff; }
`;
const PreviewImg = styled.img`width: 100%; height: 160px; object-fit: cover; border-radius: 10px; margin-top: 10px;`;

const ReviewGrid = styled.div`
  background: #f8fafc; padding: 20px; border-radius: 12px; border: 1px solid #e2e8f0;
  display: grid; grid-template-columns: 1fr 1fr; gap: 15px;
`;
const ReviewItem = styled.div`
  h4 { margin: 0 0 4px; font-size: 12px; color: #718096; text-transform: uppercase; letter-spacing: 0.5px; }
  p { margin: 0; font-size: 15px; font-weight: 600; color: #2d3748; }
`;

const ButtonGroup = styled.div`
  padding: 15px 20px; border-top: 1px solid #eee; display: flex; justify-content: space-between; background: #fff;
  
  @media (min-width: 768px) {
    padding: 20px 40px;
  }
`;

const Button = styled.button`
  padding: 10px 20px; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; border: none;
  display: flex; align-items: center; gap: 8px; transition: 0.2s;
  background: ${props => props.primary ? '#1a73e8' : '#edf2f7'};
  color: ${props => props.primary ? '#fff' : '#4a5568'};
  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { background: #cbd5e0; cursor: not-allowed; }
`;

// --- Component Start ---
const EditBusinessPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState({
    businessName: "", category: "School", tagline: "", description: "", establishedYear: "",
    email: "", phone: "", website: "", location: "",
    facebook: "", instagram: "", linkedin: ""
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (u) {
        setUser(u);
        fetchBusinessData(id);
      } else {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [id, navigate]);

  const fetchBusinessData = async (businessId) => {
    try {
      const docRef = doc(db, "businessPages", businessId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setFormData(data);
        if (data.logoUrl) setLogoPreview(data.logoUrl);
        if (data.coverImageUrl) setCoverPreview(data.coverImageUrl);
      } else {
        alert("Business page not found!");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) { 
      setFile(file); 
      setPreview(URL.createObjectURL(file)); 
    }
  };

  const validateStep = () => {
    if (activeStep === 0) {
      if (!formData.businessName || !formData.description) return alert("Business Name and Description are required.");
    }
    if (activeStep === 1) {
      if (!formData.email || !formData.phone) return alert("Email and Phone Number are required.");
    }
    setActiveStep(prev => prev + 1);
  };

  const uploadFile = async (file, path) => {
    if (!file) return null;
    const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    await new Promise((resolve, reject) => uploadTask.on("state_changed", null, reject, resolve));
    return await getDownloadURL(uploadTask.snapshot.ref);
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      let logoUrl = formData.logoUrl; 
      let coverImageUrl = formData.coverImageUrl;

      if (logoFile) logoUrl = await uploadFile(logoFile, "avaami_business_logos");
      if (coverFile) coverImageUrl = await uploadFile(coverFile, "avaami_business_covers");

      const docRef = doc(db, "businessPages", id);
      
      await updateDoc(docRef, {
        ...formData,
        logoUrl: logoUrl || null,
        coverImageUrl: coverImageUrl || null,
        updatedAt: serverTimestamp(),
      });

      alert("✅ Business Page Updated Successfully!");
      navigate(`/business/${id}`);
    } catch (error) {
      console.error(error);
      alert("Error updating page.");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { label: "Institution Info", icon: <MdStorefront /> },
    { label: "Contact Info", icon: <MdEmail /> },
    { label: "Branding", icon: <MdImage /> },
    { label: "Review & Save", icon: <MdCheckCircle /> },
  ];

  if (fetchLoading) return <Container><h3>Loading Business Details...</h3></Container>;

  return (
    <Container>
      <FormCard>
        <StepperWrapper>
          {steps.map((step, index) => (
            <StepItem key={index}>
              <StepCircle active={index === activeStep} completed={index < activeStep}>
                {index < activeStep ? <MdCheckCircle /> : index + 1}
              </StepCircle>
              <StepLabel active={index === activeStep}>{step.label}</StepLabel>
            </StepItem>
          ))}
        </StepperWrapper>

        <FormContent>
          {/* STEP 1: Details */}
          {activeStep === 0 && (
            <>
              <SectionTitle><MdBusinessCenter color="#1a73e8"/> Edit Business Details</SectionTitle>
              <Grid>
                <FormGroup>
                  <Label>Business Name</Label>
                  <Input name="businessName" value={formData.businessName} onChange={handleChange} />
                </FormGroup>
               
              </Grid>
              <FormGroup>
                <Label>Tagline</Label>
                <Input name="tagline" value={formData.tagline} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label>About Your Business</Label>
                <TextArea name="description" value={formData.description} onChange={handleChange} />
              </FormGroup>
            </>
          )}

          {/* STEP 2: Contact */}
          {activeStep === 1 && (
            <>
              <SectionTitle><MdPhone color="#1a73e8"/> Edit Contact Details</SectionTitle>
              <Grid>
                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} />
                </FormGroup>
              </Grid>
              <Grid>
                <FormGroup>
                  <Label>Website</Label>
                  <Input name="website" value={formData.website} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                  <Label>Location</Label>
                  <Input name="location" value={formData.location} onChange={handleChange} />
                </FormGroup>
              </Grid>
              <SectionTitle style={{fontSize: '1.1rem', marginTop: 20}}><MdInfo color="#1a73e8"/> Social Media Links</SectionTitle>
              <Grid>
                <FormGroup><Label>Facebook</Label><Input name="facebook" value={formData.facebook} onChange={handleChange} /></FormGroup>
                <FormGroup><Label>Instagram</Label><Input name="instagram" value={formData.instagram} onChange={handleChange} /></FormGroup>
              </Grid>
            </>
          )}

          {/* STEP 3: Images */}
          {activeStep === 2 && (
            <>
              <SectionTitle><MdImage color="#1a73e8"/> Branding & Visuals</SectionTitle>
              <Grid>
                <FormGroup>
                  <Label>Business Logo</Label>
                  <UploadBox>
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, setLogoFile, setLogoPreview)} />
                    {logoPreview ? <PreviewImg src={logoPreview} style={{height: 100, width: 100, borderRadius: '50%'}} /> : <MdStorefront size={40} color="#cbd5e0" />}
                    <span style={{marginTop: 10, color: '#718096', fontSize: '13px'}}>Click to change Logo</span>
                  </UploadBox>
                </FormGroup>
                <FormGroup>
                  <Label>Cover Image</Label>
                  <UploadBox>
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, setCoverFile, setCoverPreview)} />
                    {coverPreview ? <PreviewImg src={coverPreview} /> : <MdImage size={40} color="#cbd5e0" />}
                    <span style={{marginTop: 10, color: '#718096', fontSize: '13px'}}>Click to change Cover</span>
                  </UploadBox>
                </FormGroup>
              </Grid>
            </>
          )}

          {/* STEP 4: Review */}
          {activeStep === 3 && (
            <>
              <SectionTitle><MdCheckCircle color="#28a745"/> Review Changes</SectionTitle>
              <p style={{marginBottom: 20, color: '#718096', fontSize: '14px'}}>Verify all details before saving your updates.</p>
              
              <ReviewGrid>
                <ReviewItem><h4>Business Name</h4><p>{formData.businessName}</p></ReviewItem>
                <ReviewItem><h4>Email</h4><p>{formData.email}</p></ReviewItem>
                <ReviewItem><h4>Phone</h4><p>{formData.phone || "N/A"}</p></ReviewItem>
                <ReviewItem><h4>Location</h4><p>{formData.location || "N/A"}</p></ReviewItem>
                <ReviewItem><h4>Website</h4><p>{formData.website || "N/A"}</p></ReviewItem>
              </ReviewGrid>

              <div style={{marginTop: 20, display: 'flex', gap: 15, alignItems: 'center'}}>
                 {logoPreview && <img src={logoPreview} alt="Logo" style={{width: 70, height: 70, borderRadius: '50%', border: '2px solid #1a73e8', padding: '2px'}} />}
                 {coverPreview && <img src={coverPreview} alt="Cover" style={{width: 150, height: 70, objectFit: 'cover', borderRadius: 8, border: '2px solid #e2e8f0'}} />}
              </div>
            </>
          )}
        </FormContent>

        <ButtonGroup>
          {activeStep > 0 ? (
            <Button onClick={() => setActiveStep(prev => prev - 1)}>
              <MdNavigateBefore size={20}/> Back
            </Button>
          ) : (
            <Button onClick={() => navigate(-1)}>Cancel</Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button primary onClick={validateStep}>
              Next <MdNavigateNext size={20}/>
            </Button>
          ) : (
            <Button primary onClick={handleUpdate} disabled={loading}>
              {loading ? "Saving..." : "Save Changes"} <MdSave size={20}/>
            </Button>
          )}
        </ButtonGroup>
      </FormCard>
    </Container>
  );
};

export default EditBusinessPage;